import { Purchases } from '@revenuecat/purchases-js';
import { auth } from '../firebaseConfig';

let isConfigured = false;

// RevenueCat Web Billing Public API Key
// NOTE: You need to get this from your RevenueCat dashboard > Apps & providers > Web Billing app
// It should start with 'pk_' for production or 'rk_' for sandbox
// Production setup - uses environment variable from Vercel
const REVENUECAT_PUBLIC_API_KEY = process.env.REACT_APP_REVENUECAT_PUBLIC_KEY || 'rcb_FwUgJKyelGIXFHisYLaWOcCivURj';

/**
 * Initialize RevenueCat SDK with user authentication
 * Should be called once when a user is authenticated
 * @param {string} firebaseUid - Firebase user UID to use as appUserId
 */
export const initializeRevenueCat = async (firebaseUid = null) => {
  if (isConfigured) {
    return;
  }

  try {
    // Use Firebase UID as appUserId for identified customers
    // This enables cross-platform subscription sharing
    const purchases = Purchases.configure({
      apiKey: REVENUECAT_PUBLIC_API_KEY,
      appUserId: firebaseUid, // Identified customer approach
    });
    
    isConfigured = true;
    return purchases;
  } catch (error) {
    console.error('Failed to initialize RevenueCat SDK:', error);
    throw error;
  }
};

/**
 * Set the user ID for RevenueCat when Firebase user changes
 * Now that we configure with appUserId, this is mainly for re-initialization
 * @param {string} firebaseUid - Firebase user UID
 */
export const setRevenueCatUserId = async (firebaseUid) => {
  if (!isConfigured) {
    await initializeRevenueCat(firebaseUid);
    return;
  }

  try {
    // If already configured but need to switch users, use logIn
    await Purchases.logIn(firebaseUid);
  } catch (error) {
    console.error('Failed to set RevenueCat user ID:', error);
  }
};

/**
 * Log out the current RevenueCat user
 */
export const logOutRevenueCat = async () => {
  if (!isConfigured) {
    return;
  }

  try {
    await Purchases.logOut();
  } catch (error) {
    console.error('Failed to log out RevenueCat user:', error);
  }
};

/**
 * Check if user has access to a specific entitlement
 * @param {string} entitlementName - Name of the entitlement (e.g., 'premium')
 * @returns {Promise<boolean>} - True if user has active entitlement
 */
export const checkEntitlementAccess = async (entitlementName) => {
  if (!isConfigured) {
    await initializeRevenueCat();
  }

  try {
    const customerInfo = await Purchases.getCustomerInfo();
    const entitlement = customerInfo.entitlements.active[entitlementName];
    
    return !!entitlement;
  } catch (error) {
    console.error('Failed to check entitlement access:', error);
    return false;
  }
};

/**
 * Get all active entitlements for the current user
 * @returns {Promise<Object>} - Object containing active entitlements
 */
export const getActiveEntitlements = async () => {
  if (!isConfigured) {
    await initializeRevenueCat();
  }

  try {
    const customerInfo = await Purchases.getCustomerInfo();
    return customerInfo.entitlements.active;
  } catch (error) {
    console.error('Failed to get active entitlements:', error);
    return {};
  }
};

/**
 * Get the shared Purchases instance
 * @returns {Object} - Purchases instance
 */
export const getSharedInstance = () => {
  if (!isConfigured) {
    throw new Error('RevenueCat SDK not configured. Call initializeRevenueCat() first.');
  }
  return Purchases.getSharedInstance();
};

/**
 * Get full customer info from RevenueCat using Web SDK best practices
 * @returns {Promise<Object>} - Customer info object
 */
export const getRevenueCatCustomerInfo = async () => {
  try {
    const purchases = getSharedInstance();
    const customerInfo = await purchases.getCustomerInfo();
    return customerInfo;
  } catch (error) {
    console.error('Failed to get customer info:', error);
    return null;
  }
};

/**
 * Listen to customer info updates
 * @param {Function} callback - Callback function to handle customer info updates
 */
export const addCustomerInfoUpdateListener = (callback) => {
  if (!isConfigured) {
    console.warn('RevenueCat not configured, cannot add listener');
    return;
  }

  try {
    const purchases = Purchases.getSharedInstance();
    purchases.addCustomerInfoUpdateListener(callback);
  } catch (error) {
    console.error('Error adding customer info listener:', error);
  }
};

/**
 * Remove customer info update listener
 * @param {Function} callback - Callback function to remove
 */
export const removeCustomerInfoUpdateListener = (callback) => {
  if (!isConfigured) {
    return;
  }

  try {
    const purchases = Purchases.getSharedInstance();
    purchases.removeCustomerInfoUpdateListener(callback);
  } catch (error) {
    console.error('Error removing customer info listener:', error);
  }
};
